import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { setAuth } from '../Components/Redux/authSlice';
import { RootState } from '../Components/Redux/store';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Salaries.css';
import { Link } from 'react-router-dom';

interface Salary {
  id: number;
  teacher: number;
  teacher_name: string;
  month: string;
  amount: string;
  date_paid: string;
  branch: number;
}

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

const Salaries: React.FC = () => {
  const [salaries, setSalaries] = useState<Salary[]>([]);
  const [isEditing, setIsEditing] = useState<number | null>(null);
  const [editableSalary, setEditableSalary] = useState<Salary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        await axios.get('/users/user');
        dispatch(setAuth(true));
      } catch (e) {
        dispatch(setAuth(false));
      }
    };

    fetchData();
  }, [dispatch]);

  useEffect(() => {
    const fetchSalaries = async () => {
      try {
        const response = await axios.get('/fees/salaries/');
        setSalaries(response.data);
      } catch (err) {
        setError('Error fetching salary data');
      } finally {
        setLoading(false);
      }
    };

    fetchSalaries();
  }, []);

  const handleEditClick = async (salary: Salary) => {
    if (isEditing === salary.id && editableSalary) {
      // Validate inputs before saving
      if (!editableSalary.teacher_name || !editableSalary.month || !editableSalary.amount || !editableSalary.date_paid) {
        setError('Dhammaan meelaha waa loo baahan yahay');
        return;
      }
      try {
        const response = await axios.patch(
          `/fees/salaries/${editableSalary.id}/`,
          editableSalary,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        console.log('Server response:', response.data);
        setSalaries(salaries.map(s => (s.id === editableSalary.id ? { ...editableSalary, ...response.data } : s)));
        setError(null);
      } catch (error: any) {
        console.error('Error updating salary:', error.response ? error.response.data : error.message);
        setError('Khalad ayaa dhacay markii la cusbooneysiinayay mushaarka. Fadlan isku day markale.');
      }
      setIsEditing(null);
      setEditableSalary(null);
    } else {
      setIsEditing(salary.id);
      setEditableSalary(salary);
      setError(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (editableSalary) {
      setEditableSalary({ ...editableSalary, [name]: value });
    }
  };

  const handleDelete = async (salaryId: number) => {
    if (window.confirm('Ma hubtaa inaad rabto inaad tirtirto mushaarkan? Ficilkan lagama noqon karo.')) {
      try {
        await axios.delete(`/fees/salaries/${salaryId}/`);
        setSalaries(salaries.filter(s => s.id !== salaryId));
        setError(null);
      } catch (error: any) {
        console.error('Error deleting salary:', error.response ? error.response.data : error.message);
        setError('Khalad ayaa dhacay markii la tirtirayay mushaarka. Fadlan isku day markale.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Soo dejinaya...</span>
        </div>
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="text-center mt-5">
        <h4 className="text-gray-600 text-lg md:text-xl">Ma aadan xaqiijin</h4>
        <a href="/login" className="btn btn-primary mt-3">
          Aad Login
        </a>
      </div>
    );
  }

  return (
    <div className="salaries-container">
      {/* Hero Header */}
      <div className="salaries-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="icon">💼</span> Mushaaraadka Macalimiinta
          </h1>
          <p className="hero-subtitle">Maamul oo la soco bixinta mushaaraadka macalimiinta</p>
        </div>
        <div className="add-expense-btn ">
          <Link to="/add-salary" className="btn btn-primary">
            <i className="fas fa-plus"></i> Ku Dar Mushaar Cusub
          </Link>
        </div>
      </div>


      {/* Error Message */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Table Card */}
      <div className="table-card">
        <div className="table-header">

          <h3 className="table-title">Diiwaanka Mushaaraadka</h3>
        </div>
        <div className="table-responsive">
          <table className="modern-table">
            <thead className="thead-dark bg-gray-200">
              <tr>
                <th className="py-3 text-sm md:text-base">Magaca Macalinka</th>
                <th className="py-3 text-sm md:text-base">Bisha</th>
                <th className="py-3 text-sm md:text-base">Lacagta</th>
                <th className="py-3 text-sm md:text-base">Taariikhda Bixinta</th>
                <th className="py-3 text-sm md:text-base">Ficilada</th>
              </tr>
            </thead>
            <tbody>
              {salaries.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-3 text-sm md:text-base">
                    Ma jiro xog mushaar oo la heli karo
                  </td>
                </tr>
              ) : (
                salaries.map(salary => (
                  <tr key={salary.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="py-3 text-sm md:text-base">
                      {isEditing === salary.id ? (
                        <input
                          type="text"
                          name="teacher_name"
                          className="form-control border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
                          value={editableSalary?.teacher_name || ''}
                          onChange={handleInputChange}
                        />
                      ) : (
                        salary.teacher_name
                      )}
                    </td>
                    <td className="py-3 text-sm md:text-base">
                      {isEditing === salary.id ? (
                        <input
                          type="text"
                          name="month"
                          className="form-control border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
                          value={editableSalary?.month || ''}
                          onChange={handleInputChange}
                        />
                      ) : (
                        salary.month
                      )}
                    </td>
                    <td className="py-3 text-sm md:text-base">
                      {isEditing === salary.id ? (
                        <input
                          type="number"
                          name="amount"
                          className="form-control border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
                          value={editableSalary?.amount || ''}
                          onChange={handleInputChange}
                        />
                      ) : (
                        `${salary.amount} ksh`
                      )}
                    </td>
                    <td className="py-3 text-sm md:text-base">
                      {isEditing === salary.id ? (
                        <input
                          type="date"
                          name="date_paid"
                          className="form-control border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
                          value={editableSalary?.date_paid || ''}
                          onChange={handleInputChange}
                        />
                      ) : (
                        salary.date_paid
                      )}
                    </td>
                    <td className="py-3 text-sm md:text-base">
                      <div className="d-flex gap-2">
                        <button
                          className="edit-btn"
                          onClick={() => handleEditClick(salary)}
                        >
                          {isEditing === salary.id ? 'Keydi' : 'Wax ka bedel'}
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(salary.id)}
                        >
                          Tirtir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Salaries;